import { usePermissions } from "hooks/usePermissions";
import TooltipHorizon from "widgets/controls/tooltip";

// Type definitions for props
interface GenericButtonProps {
  loading?: boolean;
  disabled?: boolean;
  size?: "lg" | "md" | "sm" | "xs";
  variant?: "solid" | "outline" | "ghost" | "link";
  type?: "button" | "submit";
  text: string;
  onClick?: () => void;
  extra?: string;
  color?: "primary" | "secondary" | "tertiary" | "white" | string; // allow custom color values
  rounded?: string;
  permissionRole?: string;
  tooltip?: string | JSX.Element;
  tooltipPlacement?: "left" | "right" | "top" | "bottom";
  id?: string;
}

const GenericButton = ({
  loading = false,
  disabled = false,
  size = "lg",
  variant = "solid",
  type = "button",
  text,
  onClick,
  extra,
  color = "primary",
  rounded = "rounded-2xl",
  permissionRole,
  tooltip,
  tooltipPlacement = "top",
  id,
}: GenericButtonProps) => {
  const { canShow } = usePermissions();

  // Permission check
  if (permissionRole && !canShow(permissionRole)) {
    return null;
  }

  let typeClass = "";
  let sizeClass = "";
  let opacityClass = disabled || loading ? "opacity-40" : "opacity-100";

  // Named color classes
  const colorClasses = {
    primary: {
      solid: "text-white bg-primary hover:opacity-50",
      outline:
        "border-2 border-primary text-primary bg-transparent hover:opacity-50",
      ghost: "text-primary bg-transparent hover:opacity-50",
      link: "text-primary underline hover:opacity-50",
    },
    secondary: {
      solid: "text-white bg-secondary hover:opacity-50",
      outline:
        "border-2 border-secondary text-secondary bg-transparent hover:opacity-50",
      ghost: "text-secondary bg-transparent hover:opacity-50",
      link: "text-secondary underline hover:opacity-50",
    },
    tertiary: {
      solid: "text-white bg-tertiary hover:opacity-50",
      outline:
        "border-2 border-tertiary text-tertiary bg-transparent hover:opacity-50",
      ghost: "text-tertiary bg-transparent hover:opacity-50",
      link: "text-tertiary underline hover:opacity-50",
    },
    white: {
      solid: "text-primary bg-white hover:opacity-50",
      outline:
        "border-2 border-white text-white bg-transparent hover:opacity-50",
      ghost: "text-white bg-transparent hover:opacity-50",
      link: "text-white underline hover:opacity-50",
    },
  } as const;

  // Type guard for named colors
  function isNamedColorKey(color: string): color is keyof typeof colorClasses {
    return color in colorClasses;
  }

  // Determine the class based on color and variant
  typeClass = isNamedColorKey(color)
    ? colorClasses[color][variant]
    : colorClasses["primary"][variant];

  // Size classes
  switch (size) {
    case "lg":
      sizeClass = "h-12 px-6 heading-md";
      break;
    case "md":
      sizeClass = "h-10 px-4 heading-sm";
      break;
    case "sm":
      sizeClass = "h-8 px-3 heading-sm";
      break;
    case "xs":
      sizeClass = "h-6 px-2 heading-xs";
      break;
  }

  // Button element
  const button = (
    <button
      className={` ${rounded} ${opacityClass} ${sizeClass} ${typeClass} ${extra}`}
      disabled={loading || disabled}
      onClick={onClick}
      type={type}
      id={id}
    >
      {text}
    </button>
  );

  // Tooltip wrapper if needed
  if (tooltip) {
    return (
      <TooltipHorizon
        trigger={button}
        content={typeof tooltip === "string" ? <span>{tooltip}</span> : tooltip}
        placement={tooltipPlacement}
        extra=""
      />
    );
  }

  return button;
};

export default GenericButton;
